<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import { getStatusLabel } from '@/helpers/documentSubmission.helper';
import { getFullName } from '@/helpers/text.helper';
import { formatDate } from '@/plugins/i18n';
import type { DocumentSubmissionVersionDto } from '@/services/types/api.types';

// PROPS
const props = defineProps<{
  versions: DocumentSubmissionVersionDto[];
  publishedVersionId: number | null;
  selectedVersionId: number | null;
}>();

// EMITS
const emit = defineEmits<{ 'update:selectedVersionId': [id: number] }>();

// CONST
const { t } = useI18n();

// FUNCTIONS
const personName = (person: DocumentSubmissionVersionDto['reviewedBy']) => getFullName(person) || '—';
</script>

<template>
  <q-list bordered separator class="document-submission-version-history">
    <q-item
      v-for="version in props.versions"
      :key="version.id"
      clickable
      :active="version.id === props.selectedVersionId"
      active-class="document-submission-version-history__item--selected"
      class="document-submission-version-history__item"
      @click="emit('update:selectedVersionId', version.id)"
    >
      <q-item-section>
        <div class="row items-center q-gutter-x-sm">
          <span class="text-weight-bold">v{{ version.version_number }}</span>
          <span class="text-grey-7">{{ formatDate(version.created_at) }}</span>
          <q-badge
            outline
            color="grey-8"
            :label="getStatusLabel(version.status)"
            class="document-submission-version-history__status"
          />
          <q-badge
            v-if="version.id === props.publishedVersionId"
            color="positive"
            :label="t('project-module.documentSubmission.dialogs.public')"
            class="document-submission-version-history__public"
          />
        </div>
        <div class="text-body1 q-mt-xs">{{ version.title }}</div>

        <div
          v-if="version.purged_at"
          class="document-submission-version-history__tombstone text-grey-7 text-italic q-mt-xs"
        >
          {{
            t('project-module.documentSubmission.dialogs.purgedOn', {
              date: formatDate(version.purged_at),
              name: personName(version.purgedBy),
            })
          }}
        </div>
        <a
          v-else-if="version.file"
          :href="version.file.url"
          target="_blank"
          rel="noopener noreferrer"
          class="document-submission-version-history__file q-mt-xs"
          @click.stop
        >
          {{ version.file.name }}
        </a>

        <!-- A purge deletes the comment along with the file. -->
        <div v-if="!version.purged_at" class="q-mt-sm">
          <div class="text-caption text-grey-7">{{ t('project-module.documentSubmission.dialogs.comment') }}</div>
          <div v-if="version.participant_comment" class="document-submission-version-history__text">
            {{ version.participant_comment }}
          </div>
          <div v-else class="text-grey-7">{{ t('project-module.documentSubmission.dialogs.noComment') }}</div>
        </div>

        <div v-if="version.reviewed_at" class="document-submission-version-history__decision q-mt-sm">
          <div class="text-caption text-grey-7">
            {{
              t('project-module.documentSubmission.dialogs.reviewedBy', {
                name: personName(version.reviewedBy),
                date: formatDate(version.reviewed_at),
              })
            }}
          </div>
          <div v-if="version.review_note" class="document-submission-version-history__text">
            {{ version.review_note }}
          </div>
        </div>
      </q-item-section>
    </q-item>
  </q-list>
</template>

<style lang="scss">
.document-submission-version-history {
  border-radius: 4px;

  // Nested once, to outrank the primary text colour of Quasar's `.q-item--active`: the bar marks the selection.
  & &__item--selected {
    background: rgba(0, 0, 0, 0.04);
    box-shadow: inset 3px 0 0 $color-primary;
    color: inherit;
  }

  &__text {
    white-space: pre-line;
    overflow-wrap: anywhere;
  }
}
</style>
